// Pure helpers for the /qa_scores page: query building, score lookup and CSV rows.

import moment from 'moment-timezone';

export const QA_PAGE_SIZE = 50;
export const QA_PROFILE_TEACHER_LIMIT = 10;

export const CATEGORIES = [
  { t: 'energy', p: ['energy level'] },
  {
    t: 'welcome',
    p: [
      'warm greeting',
      'friendly smile',
      'welcoming',
      'session breakdown',
      'effective introduction'
    ]
  },
  {
    t: 'interview',
    p: [
      'open ended question',
      'reflections / affirmations',
      'summarizing',
      'non verbal cues',
      'no interruption'
    ]
  },
  { t: 'practice plan', p: ['session plan', 'intensity of practice'] },
  { t: 'practice', p: ['demo and explanation', 'affirmations'] },
  {
    t: 'feedback & de-brief',
    p: ['personalized feedback', 'summarizing the session', 'inviting questions & doubt clearing']
  },
  { t: 'future plan', p: ['future plan'] },
  { t: 'concierge handoff', p: ['handoff'] }
];

// Builds the qascores query string for the page mode: student link, teacher profile link, or filters.
export const buildQaScoresQuery = ({
  studentUuid,
  profileTeacherUuid,
  teacher,
  since,
  till,
  timezone,
  page = 1,
  exportAll = false
}) => {
  if (studentUuid) return new URLSearchParams({ student_uuid: studentUuid }).toString();
  if (profileTeacherUuid) {
    return new URLSearchParams({
      teacher_uuid: profileTeacherUuid,
      limit: String(QA_PROFILE_TEACHER_LIMIT)
    }).toString();
  }
  const qp = new URLSearchParams({ since, till });
  if (teacher && teacher !== 'all') qp.append('teacher_uuid', teacher);
  if (timezone) qp.append('timezone', timezone);
  if (exportAll) {
    qp.append('export', 'true');
  } else {
    qp.append('page', String(page));
    qp.append('limit', String(QA_PAGE_SIZE));
  }
  return qp.toString();
};

// Score categories/parameters are matched lower-case; everything else is left as sent.
export const normalizeScoreRow = (row) => ({
  ...row,
  qa_score: (row.qa_score || []).map((s) => ({
    ...s,
    category: (s.category || '').toLowerCase(),
    parameter: (s.parameter || '').toLowerCase()
  }))
});

export const lookupScore = (rscores, category, point) => {
  const found = (rscores || []).find((row) => row.parameter === point && row.category === category);

  if (found === undefined) {
    // Older scores split "effective introduction" into three parameters.
    if (category === 'welcome' && point === 'effective introduction') {
      const c1 = lookupScore(rscores, category, 'coach life story');
      const c2 = lookupScore(rscores, category, 'relavant expertise');
      const c3 = lookupScore(rscores, category, 'students helped');

      return {
        score: c1.score + c2.score + c3.score,
        reason: [c1.reason, c2.reason, c3.reason].join(',')
      };
    }

    return { score: 0, reason: 'No score found' };
  }

  return found;
};

// Old-format rows have no reliable total_score, so their parameter scores are summed.
export const rowTotal = (row) => {
  const isNewScore =
    lookupScore(row.qa_score, 'welcome', 'coach life story').reason === 'No score found';
  if (isNewScore) return row.total_score;
  return (row.qa_score || []).reduce((sum, cps) => sum + cps.score, 0);
};

export const formatSessionDate = (sessionStart, ianaTimezone) => {
  if (!sessionStart) return '';
  const m = moment(sessionStart);
  return (ianaTimezone ? m.tz(ianaTimezone) : m).format('YYYY-MM-DD HH:mm z');
};

export const QA_CSV_HEADERS = [
  'Session UUID',
  'Session Date',
  'Student',
  'Coach',
  'Total',
  ...CATEGORIES.flatMap((c) => c.p.map((p) => `${c.t}: ${p}`))
];

// Maps normalized rows into QA_CSV_HEADERS order.
export const buildQaCsvRows = (rows, ianaTimezone) =>
  (rows || []).map((row) => [
    row.session_uuid || '',
    formatSessionDate(row.session_start_time, ianaTimezone),
    row.student_name || '',
    row.teacher_name || '',
    rowTotal(row),
    ...CATEGORIES.flatMap((c) => c.p.map((p) => lookupScore(row.qa_score, c.t, p).score))
  ]);
